import React, { useState,useEffect } from "react";
import { AddButton, DeleteButton } from "../../../../components/common/Button";
import { Alert, Button, Input, Switch, message } from "antd";
import { QuestionsGenerator } from "./QuestionsGenerator";
import { TablesGenerator } from "./TablesGenerator";
import { useDispatch, useSelector } from "react-redux";
import {
  setReportForm,
  setSwitchState,
  setEmissionSaved,
  resetReportForm,
  resetEmissionSaved,
  setisReset,
  setisCalculated,
  setIsQuestionCalculated,
  resetIsQuestionCalculated
} from "../../../../redux/slices/supplierReportingSlice";
import { apiRequest } from "../../../../services/apiService";
import { BUTTON_CLASSES,AlertMessages,QUESTION_BORDER_STYLE, TEXT_CONSTANTS } from "../../../../constants/Constants";

const TabsGenerator = ({ Data }) => {
  const dispatch = useDispatch();
  const { ReportForm, SwitchState, emissionSaved,isReset,isCalculated,isQuestionCalculated } = useSelector(
    (state) => state.reporting
  );

  const [duplicatedQuestions, setDuplicatedQuestions] = useState({});
  const [calculatedQuestions, setCalculatedQuestions] = useState({});

  // Initialize duplicated questions from prepopulated data
  useEffect(() => {
    if (ReportForm[Data.pillarId]) {
      // Check for duplicate questions that were prepopulated
      const newDuplicatedQuestions = {};
      
      Object.keys(ReportForm[Data.pillarId]).forEach(questionId => {
        if (questionId.includes('_duplicate_')) {
          const baseQuestionId = questionId.split('_duplicate_')[0];
          const duplicateIndex = parseInt(questionId.split('_duplicate_')[1]);
          
          if (!newDuplicatedQuestions[baseQuestionId]) {
            newDuplicatedQuestions[baseQuestionId] = [];
          }
          
          // Add the duplicate index if it doesn't exist already
          if (!newDuplicatedQuestions[baseQuestionId].includes(duplicateIndex)) {
            newDuplicatedQuestions[baseQuestionId].push(duplicateIndex);
          }
        }
      });
      
      // Set the duplicated questions based on prepopulated data
      setDuplicatedQuestions(newDuplicatedQuestions);
      
      // Also initialize calculated questions for prepopulated data
      const newCalculatedQuestions = {};
      Object.keys(ReportForm[Data.pillarId]).forEach(questionId => {
        if (emissionSaved[Data.pillarId]?.[questionId]) {
          newCalculatedQuestions[`${Data.pillarId}_${questionId}`] = true;
        }
      });
      
      setCalculatedQuestions(prev => ({
        ...prev,
        ...newCalculatedQuestions
      }));
    }
  }, [ReportForm, Data.pillarId, emissionSaved]);

  const handleInputChange = (pillarId, questionId, inputId, value, duplicateIndex = null, rowId = null, type = "QUESTION") => {
    if (type !== 'TABLE' && (pillarId === "Energy" || pillarId === "Packaging")) {
      dispatch(setisCalculated(false));
    }
    const finalQuestionId = duplicateIndex !== null
      ? `${questionId}_duplicate_${duplicateIndex}`
      : questionId;

    // Store the new value first for comparison
    const updatedInputFields = {
      ...(ReportForm[pillarId]?.[finalQuestionId]?.inputFields || {}),
      [inputId]: value
    };

    // Check if all field values would match any existing question after this change
    if (duplicateIndex !== null) {
      // Get the question definition to know all required fields
      const question = Data.reportQuestions.find(q => q.questionId === questionId);
      if (!question) return;
      
      const requiredFields = question.inputFields.map(f => f.inputId);
      
      // Only perform the check if all fields have values in the current form
      const allFieldsFilled = requiredFields.every(field => 
        field === inputId ? value !== null && value !== "" : updatedInputFields[field]
      );
      
      if (allFieldsFilled) {
        // Find all other instances of this question (original and duplicates)
        const allRelatedQuestionIds = [questionId];
        
        if (duplicatedQuestions[questionId]) {
          duplicatedQuestions[questionId].forEach(idx => {
            if (idx !== duplicateIndex) {
              allRelatedQuestionIds.push(`${questionId}_duplicate_${idx}`);
            }
          });
        }
        
        // Check if any other instance has exactly the same values for all fields
        const isDuplicate = allRelatedQuestionIds.some(qId => {
          const existingFields = ReportForm[pillarId]?.[qId]?.inputFields;
          if (!existingFields) return false;
          
          // Check if all field values match
          return requiredFields.every(field => {
            const existingValue = existingFields[field];
            const newValue = field === inputId ? value : updatedInputFields[field];
            return existingValue !== undefined && existingValue === newValue;
          });
        });
        
        if (isDuplicate) {
          message.warning("Duplicate entries are not allowed. Please enter unique values for this question.");
          return;
        }
      }
    }

    dispatch(setReportForm({
      pillarId,
      questionId: finalQuestionId,
      inputId,
      rowId, 
      value
    }));
    if((pillarId==="Packaging" && questionId !=="qstn_id_4") || pillarId !=="Waste"){
    dispatch(setIsQuestionCalculated({pillarId,questionId:finalQuestionId,value:true}));
    }
    
  };

  const handleCalculation = async (pillarId, questionId, ReportForm, duplicateIndex = null) => {
    
    if(pillarId==="Energy" || pillarId==="Packaging"){
      dispatch(setisCalculated(true));
    }
    
    const finalQuestionId = duplicateIndex !== null
      ? `${questionId}_duplicate_${duplicateIndex}`
      : questionId;

    const inputs = ReportForm[pillarId]?.[finalQuestionId]?.inputFields;

    if (!inputs || Object.values(inputs).some(value => !value)) {
      message.error(AlertMessages.MISSING_FIELDS);
      return;
    }

    // Check for duplicate entries before calculation
    if (duplicateIndex !== null) {
      const question = Data.reportQuestions.find(q => q.questionId === questionId);
      if (!question) return;
      
      const requiredFields = question.inputFields.map(f => f.inputId);
      
      // Find all other instances of this question (original and duplicates)
      const allRelatedQuestionIds = [questionId];
      
      if (duplicatedQuestions[questionId]) {
        duplicatedQuestions[questionId].forEach(idx => {
          if (idx !== duplicateIndex) {
            allRelatedQuestionIds.push(`${questionId}_duplicate_${idx}`);
          }
        });
      }
      
      // Check if any other instance has exactly the same values for all fields
      const isDuplicate = allRelatedQuestionIds.some(qId => {
        const existingFields = ReportForm[pillarId]?.[qId]?.inputFields;
        if (!existingFields) return false;
        
        // Only compare if all required fields exist
        const hasAllFields = requiredFields.every(field => existingFields[field] !== undefined);
        if (!hasAllFields) return false;
        
        // Check if all field values match
        return requiredFields.every(field => 
          existingFields[field] === inputs[field]
        );
      });
      
      if (isDuplicate) {
        message.warning("Duplicate entries are not allowed. Please enter unique values for this question.");
        return;
      }
    }

    const lookupPayload = {
      pillarId: pillarId,
      questionId: questionId,
      inputFields: inputs,
    };

    try {
      const result = await apiRequest("POST", "/lookup/calculate", lookupPayload);
      const calculatedValue = result?.calculatedValue;

      if (calculatedValue) {
        dispatch(
          setEmissionSaved({
            pillarId,
            questionId: finalQuestionId,
            value: calculatedValue
          })

        );
        dispatch(setIsQuestionCalculated({pillarId,questionId:finalQuestionId,value:true}))

        setCalculatedQuestions(prev => ({
          ...prev,
          [`${pillarId}_${finalQuestionId}`]: true
        }));
       
      } else {
        message.error(AlertMessages.CALCULATION_INCOMPLETE);
      }
    } catch (error) {
      console.error("Error calculating:", error);
      message.error(AlertMessages.SAVE_REQUIRED);
    }
  };

  // Modified to allow adding questions when pre-populated
  const canAddQuestion = (pillarId, questionId, currentDuplicateIndex = null) => {
    const finalQuestionId = currentDuplicateIndex !== null
      ? `${questionId}_duplicate_${currentDuplicateIndex}`
      : questionId;
      
    // Check if the question has been calculated (either from preloaded data or by user action)
    const isQuestionDone = isQuestionCalculated?.[pillarId]?.[finalQuestionId] || 
                          calculatedQuestions[`${pillarId}_${finalQuestionId}`];
    
    if (!isQuestionDone) {
        return false;
    }

    // For duplicated questions, check if previous questions are calculated
    if (currentDuplicateIndex !== null) {
     for (let i = 0; i < currentDuplicateIndex; i++) {
        const prevQuestionId = `${questionId}_duplicate_${i}`;
        const isPrevQuestionDone = isQuestionCalculated?.[pillarId]?.[prevQuestionId] || 
         calculatedQuestions[`${pillarId}_${prevQuestionId}`];

    if (!isPrevQuestionDone) {
      return false;
    }
     }
    }

    return true;
  };

  const handleAddQuestion = (pillarId, questionId, currentDuplicateIndex = null) => {
    if (!canAddQuestion(pillarId, questionId, currentDuplicateIndex)) {
      message.warning(AlertMessages.WARNING_ADD_QUESTION);
      return;
    }
    
    const currentDuplicates = duplicatedQuestions[questionId] || [];
    
    // Find the next available index (might not be sequential if questions were deleted)
    let newIndex = 0;
    while (currentDuplicates.includes(newIndex)) {
      newIndex++;
    }
    
    // If there are higher indices, use the next number after the highest
    if (currentDuplicates.length > 0) {
      const maxIndex = Math.max(...currentDuplicates);
      newIndex = maxIndex + 1;
    }

    setDuplicatedQuestions(prev => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), newIndex]
    }));
  };

  const handleDeleteQuestion = (questionId, duplicateIndex) => {
    const finalQuestionId = `${questionId}_duplicate_${duplicateIndex}`;

    // Reset the input fields first before removing from Redux store
    const question = Data.reportQuestions.find(q => q.questionId === questionId);
    if (question && question.inputFields) {
      question.inputFields.forEach(input => {
        handleInputChange(Data.pillarId, questionId, input.inputId, "", duplicateIndex);
      });
    }
    
    // Remove data from ReportForm
    dispatch(resetReportForm({
      pillarId: Data.pillarId,
      questionId: finalQuestionId
    }));
    dispatch(resetIsQuestionCalculated({
      pillarId: Data.pillarId,
      questionId:finalQuestionId
    }))
    

    // Remove emission saved data
    dispatch(resetEmissionSaved({
      pillarId: Data.pillarId,
      questionId: finalQuestionId
    }));

    if (duplicateIndex !== null) {
      setDuplicatedQuestions(prev => ({
        ...prev,
        [questionId]: prev[questionId].filter(idx => idx !== duplicateIndex)
      }));
    }

    setCalculatedQuestions(prev => {
      const newState = { ...prev };
      delete newState[`${Data.pillarId}_${finalQuestionId}`];
      return newState;
    });
  };
  useEffect(() => {
    if (isReset) {
      Data.reportQuestions
        .filter(question => question.type === "QUESTION")
        .forEach(question => {
          handleSwitchChange(Data.pillarId, question.questionId, false);
        });
      dispatch(setisReset(false));
    }
  }, [isReset, Data.pillarId, Data.reportQuestions, dispatch]);

  const handleSwitchChange = (pillarId, questionId, value) => {
    dispatch(setSwitchState({ pillarId, questionId, value }));
    
    if (!value) {
      resetQuestionValues(pillarId, questionId);
      dispatch(resetReportForm({
        pillarId,
        questionId
      }));
      dispatch(resetIsQuestionCalculated({
        pillarId,
        questionId
      }))
      
      dispatch(resetEmissionSaved({
        pillarId,
        questionId
      }));

      // Clean up duplicated questions if they exist
      if (duplicatedQuestions[questionId]) {
        duplicatedQuestions[questionId].forEach(duplicateIndex => {
          const duplicateQuestionId = `${questionId}_duplicate_${duplicateIndex}`;
          
          dispatch(resetReportForm({
            pillarId,
            questionId: duplicateQuestionId
          }));
          dispatch(resetIsQuestionCalculated({
            pillarId,
            questionId: duplicateQuestionId
          }))

          
          dispatch(resetEmissionSaved({
            pillarId,
            questionId: duplicateQuestionId
          }));
        });

        // Reset duplicated questions state
        setDuplicatedQuestions(prev => ({
          ...prev,
          [questionId]: []
        }));
      }

      // Reset calculated questions state
      setCalculatedQuestions(prev => {
        const newState = { ...prev };
        delete newState[`${pillarId}_${questionId}`];
        if (duplicatedQuestions[questionId]) {
          duplicatedQuestions[questionId].forEach(duplicateIndex => {
            delete newState[`${pillarId}_${questionId}_duplicate_${duplicateIndex}`];
          });
        }
        return newState;
      });
    }
  };

  const resetQuestionValues = (pillarId, questionId, duplicateIndex = null) => {
    const finalQuestionId = duplicateIndex !== null
      ? `${questionId}_duplicate_${duplicateIndex}`
      : questionId;

    const question = Data.reportQuestions.find(q => q.questionId === questionId);
    question.inputFields.forEach(input => {
      handleInputChange(pillarId, questionId, input.inputId, "", duplicateIndex, null, question.type);
    });

    setCalculatedQuestions(prev => ({
      ...prev,
      [`${pillarId}_${finalQuestionId}`]: false
    }));

    dispatch(setEmissionSaved({
      pillarId,
      questionId: finalQuestionId,
      value: null
    }));
  };

  const renderQuestionForm = (question, duplicateIndex = null) => {
    const finalQuestionId = duplicateIndex !== null
      ? `${question.questionId}_duplicate_${duplicateIndex}`
      : question.questionId;

    const isDefaultQuestion = duplicateIndex === null;

    return (
      <div className="flex flex-col">
        <div className="flex justify-between items-start">
          <div className={`flex px-[5px] gap-2 ${QUESTION_BORDER_STYLE}`}>
            {QuestionsGenerator(
              question.text,
              question.inputFields,
              finalQuestionId,
              (pillarId, qId, inputId, value) =>
                handleInputChange(pillarId, question.questionId, inputId, value, duplicateIndex),
              Data.pillarId,
              ReportForm
            )}
          </div>
          <div className="ml-[10px] flex flex-col gap-1 justify-center">
            <AddButton
              onClick={() => handleAddQuestion(Data.pillarId, question.questionId, duplicateIndex)}
              className={!canAddQuestion(Data.pillarId, question.questionId, duplicateIndex) ? BUTTON_CLASSES.addButtonDisabled : ''}
            />
            <DeleteButton
              onClick={() =>
                isDefaultQuestion
                  ? resetQuestionValues(Data.pillarId, question.questionId)
                  : handleDeleteQuestion(question.questionId, duplicateIndex)
              }
            />
          </div>
        </div>

        <div className="mt-2">
          {emissionSaved?.[Data.pillarId]?.[finalQuestionId] && (
            <h1 className="mt-4 mb-4 text-[15px] font-semibold">
              {TEXT_CONSTANTS.EMISSIONS_SAVED}:
              <Input
                className="min-w-[75px] max-w-[100px] bg-[#EAF5FF] ml-[4px]"
                value={`${emissionSaved[Data.pillarId][finalQuestionId]}   ${TEXT_CONSTANTS.METRIC_TON}`}
                readOnly
              />
            </h1>
          )}
          <Button
            className="bg-[#1F405A] text-white"
            onClick={() =>
              handleCalculation(
                Data.pillarId,
                question.questionId,
                ReportForm,
                duplicateIndex
              )
            }
          >
            Calculate
          </Button>
        </div>
      </div>
    );
  };

  const renderQuestions = () => {
    return Data?.reportQuestions
      ?.filter((question) => question.type === "QUESTION")
      ?.map((question, index) => (
        <div key={index} className="w-auto flex p-4">
          <div className="question-container border-grey border-b-[1.5px] pb-5 flex-1 ml-5">
            <div className="question-box flex items-center">
              <p className="question-text font-semibold">
                {question.questionId.charAt(8)}. {question.question}
              </p>
              <div className="ml-3 pr-5">
                <Switch
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  onChange={(value) =>
                    handleSwitchChange(
                      Data.pillarId,
                      question.questionId,
                      value
                    )
                  }
                  checked={SwitchState?.[Data.pillarId]?.[question.questionId] || false}
                />
              </div>
            </div>

            {SwitchState?.[Data.pillarId]?.[question.questionId] && (
              <div className="mt-[5px] ml-[15px] space-y-4">
                {renderQuestionForm(question)}
                {duplicatedQuestions[question.questionId]?.sort((a, b) => a - b).map((duplicateIndex) => (
                  <div key={`${question.questionId}_duplicate_${duplicateIndex}`}>
                    {renderQuestionForm(question, duplicateIndex)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ));
  };

  const renderTables = () => {
    return Data?.reportQuestions
      ?.filter((question) => question.type === "TABLE")
      ?.map((table, tableIndex) => (
        <div key={tableIndex} className="mt-3 px-[35px]">
          <p className="question-text pl-3 pb-3 font-semibold">
            {table.text}
          </p>
          {TablesGenerator({
            questionId: table.questionId,
            tableHeader: table.inputFields.map((field) => field.tableHeader),
            rowInputs: table.inputFields,
            pillarId: Data.pillarId,
            handleInputChange,
            ReportForm,
          })}
          <div className="mt-2 ml-[15px]">
            {emissionSaved?.[Data.pillarId]?.[table.questionId] && (
              <h1 className="mt-4 mb-4 font-semibold">
                EmissionSaved:
                <Input
                  className="w-[75px] bg-[#EAF5FF] ml-[4px]"
                  value={emissionSaved[Data.pillarId][table.questionId]}
                  readOnly
                />
              </h1>
            )}
          </div>
        </div>
      ));
  };

  return (
    <div>
      {renderQuestions()}
      {renderTables()}
    </div>
  );
};

export default TabsGenerator;